// What the call window shows, as pure functions of the call, its CRM card, the transcript and the coach. Every slot
// gets a value in every phase (booting, starting, ringing, active, held, ended, failed, signed out, unavailable), so
// the window renders one DOM tree for the whole call and only the text inside a slot changes. Nothing here reads
// CoachState.thinking: a "Coach is thinking" line that comes and goes is exactly the movement Allen asked to remove.
import { CALL_SUMMARY_MIN_TALK_MS, type Call, type CoachResponse, type Transcript } from '@bcio-voice/contracts';
import { formatDuration, formatPhone, formatRelative } from '../../lib/format.ts';
import { type CoachState, coachOffHoursLine } from '../../call/coach/coach.ts';
import { committedPrefix } from '../transcript/liveTranscript.ts';
import type { ContextCardModel, ContextLink } from '../phone/contextCard.ts';

export type CallWindowPhase = 'booting' | 'pending' | 'ready' | 'failed_start' | 'signed_out' | 'unavailable' | 'moved';

/** The call as this rep experiences it: once my own leg has left (a blind transfer, leaving a 3-way), it is over for me. */
export function effectiveState(call: Pick<Call, 'state' | 'participants'> | null, selfUserId: string | null): Call['state'] | null {
  if (!call) return null;
  const me = selfUserId ? call.participants.find(p => p.user_id === selfUserId && p.kind !== 'external') : null;
  if (me?.left_at && (call.state === 'active' || call.state === 'held' || call.state === 'ringing' || call.state === 'created')) return 'ended';
  return call.state;
}

export type Tone = 'live' | 'ring' | 'hold' | 'warn' | 'end' | 'neutral';

/** The header's state word, beside the timer. */
export function headState(phase: CallWindowPhase, call: Call | null, selfUserId: string | null): { word: string; tone: Tone } {
  if (phase === 'signed_out') return { word: 'Signed out', tone: 'warn' };
  if (phase === 'unavailable') return { word: 'Not available', tone: 'warn' };
  if (phase === 'failed_start') return { word: 'Didn’t start', tone: 'end' };
  if (phase === 'moved') return { word: 'Moved', tone: 'neutral' };
  if (!call) return { word: 'Starting', tone: 'neutral' };
  const me = selfUserId ? call.participants.find(p => p.user_id === selfUserId && p.kind !== 'external') : null;
  const state = effectiveState(call, selfUserId);
  if (state === 'ended' && me?.left_at && call.state !== 'ended' && call.state !== 'failed') {
    const label = call.transfer?.target_label || (call.transfer?.target_destination.kind === 'pstn' ? formatPhone(call.transfer.target_destination.e164) : '');
    return { word: label ? `Transferred to ${label}` : 'You left the call', tone: 'neutral' };
  }
  if (call.transfer && (call.state === 'active' || call.state === 'held')) {
    if (call.transfer.state === 'ringing') return { word: 'Transferring', tone: 'ring' };
    if (call.transfer.state === 'consulting') return { word: 'Consulting', tone: 'hold' };
    if (call.transfer.state === 'merged') return { word: '3-way', tone: 'live' };
  }
  switch (call.state) {
    case 'created': return { word: 'Starting', tone: 'neutral' };
    case 'ringing': return { word: 'Ringing', tone: 'ring' };
    case 'active': return { word: 'Connected', tone: 'live' };
    case 'held': return { word: 'On hold', tone: 'hold' };
    case 'dropping': return { word: 'Ending', tone: 'neutral' };
    case 'failed': return { word: 'Failed', tone: 'end' };
    default: return { word: 'Ended', tone: 'neutral' };
  }
}

/** The far end's number, formatted. */
export function partyNumber(call: Pick<Call, 'direction' | 'from' | 'destination'> | null): string {
  if (!call) return '';
  const e164 = call.direction === 'inbound' ? call.from.e164 : call.destination.kind === 'pstn' ? call.destination.e164 : null;
  return e164 ? formatPhone(e164) || e164 : '';
}

/** Header name and the line under it: the CRM card's contact first, then the card title, the contact, the number. */
export function headerModel(call: Call | null, card: ContextCardModel | null): { name: string; sub: string } {
  const number = partyNumber(call);
  const who = card?.who ?? null;
  const cardTitle = card && card.kind !== 'unknown' ? card.title : null;
  const name = who?.contactName || cardTitle || call?.contact_display_name || number || '';
  const subParts = [who?.company && who.company !== name ? who.company : null, who?.contactRole ?? null].filter((s): s is string => !!s);
  const sub = subParts.length ? subParts.join(', ') : name !== number ? number : '';
  return { name, sub };
}

export function timerText(call: Pick<Call, 'created_at' | 'answered_at' | 'ended_at' | 'talk_ms' | 'state'> | null, now: number): string {
  if (!call) return '0:00';
  const start = Date.parse(call.created_at);
  if (!Number.isFinite(start)) return '0:00';
  const end = call.ended_at ? Date.parse(call.ended_at) : NaN;
  const stop = (call.state === 'ended' || call.state === 'failed') && Number.isFinite(end) ? end : now;
  return formatDuration(Math.max(0, stop - start));
}

// ---- Transcript ----

export type SpeakerRole = 'caller' | 'agent' | 'other';
/**
 * One speaker turn (consecutive segments of one participant), keyed by its first segment. text: the locked words in
 * full colour; a final shows its final words, so when the final decode heard locked words differently the row's words
 * are replaced in place (Allen, November 1, 2026: "accuracy is a thing still"). pending: the newest segment's unlocked
 * tail, which the view writes muted on the transcript's reserved last line, never in the row. partial: the turn's
 * newest segment is still in progress. Present only on a row whose words a correction replaced: fit, the words the
 * row's box is sized for (the words it showed before, when they differ from text), so a correction never changes the
 * row's height and no row under it moves; held, nothing has joined the row since: its box is exactly fit's and words
 * that need more lines are cut at its last line. Once new words join, the box holds the longer of fit and text, so it
 * never shrinks and the newest words are never cut. corrections: how many times its words were replaced in place (the
 * view's settle restarts on each).
 */
export type TranscriptRow =
  | { key: string; kind: 'seg'; role: SpeakerRole; who: string; text: string; pending: string; partial: boolean; revision: number; fit?: string; held?: boolean; corrections?: number }
  | { key: string; kind: 'gap'; text: string };
type SegRow = Extract<TranscriptRow, { kind: 'seg' }>;

type Segment = Transcript['segments'][number];
type Participant = Call['participants'][number];

/** The first name to label the prospect with: the card's contact name without a title ("Dr. Ada Lovelace" is Ada). */
export function firstName(contactName: string | null | undefined): string | null {
  const words = (contactName ?? '').trim().split(/\s+/).filter(Boolean);
  while (words.length > 1 && /^(?:dr|mr|mrs|ms|mx|miss|prof|sir|dame)\.?$/i.test(words[0]!)) words.shift();
  const first = words[0]?.replace(/[,;:]+$/, '') ?? '';
  return first && /\p{L}/u.test(first) ? first : null;
}

/** Caller is the external party (by the card's first name when the card has one); You is my own agent leg; anyone else
 * (a teammate, a supervisor) is muted like an agent. */
export function speakerOf(participants: readonly Participant[], participantId: string | null, selfUserId: string | null, callerName: string | null = null): { role: SpeakerRole; who: string } {
  const p = participantId ? participants.find(x => x.id === participantId) : undefined;
  if (!p) return { role: 'other', who: 'Speaker' };
  if (p.kind === 'external') return { role: 'caller', who: callerName || 'Caller' };
  if (p.kind === 'supervisor') return { role: 'other', who: 'Coach' };
  return p.user_id && p.user_id === selfUserId ? { role: 'agent', who: 'You' } : { role: 'other', who: 'Teammate' };
}

/** What a segment's row shows: text, a final's words (its newest revision's once it is shown) or a partial's locked
 * prefix (never shorter than one already shown while the text still starts with it). rev: the revision a final's words
 * were taken from. fit: once a correction replaced words the row showed (a shown final's, or a partial's locked words
 * replaced by a final that heard them differently), the words the row's box stays sized for (the words as first
 * shown), kept only while they differ from text. corrections: how many times this segment's shown words were replaced
 * in place. shownAt and correctedAt order what rows showed (one counter for the whole map): shownAt when the segment's
 * words last changed other than by a correction (first shown, locked words added, a final landing), correctedAt its
 * first correction; so a row knows whether words joined it after it was corrected, whichever of its segments was.
 * row: the key of the row (speaker turn) the segment was first shown in, and seq: that row's place, in the order rows
 * first appeared. A gap row has an entry too (key gapKey, empty text). */
export interface DisplayedSegment { text: string; final: boolean; row?: string; seq?: number; rev?: number; fit?: string; corrections?: number; shownAt?: number; correctedAt?: number }
export type DisplayedText = ReadonlyMap<string, DisplayedSegment>;
export const NO_DISPLAYED: DisplayedText = new Map();
const gapKey = (g: Transcript['gaps'][number]) => `gap:${g.start_ms}-${g.end_ms}`;

/**
 * Locked words repaint in place only when the final decode disagrees (docs/design/CALL-POP-KRISP-BAR.md principle 2;
 * Allen, November 1, 2026: "yes, i mean - accuracy is a thing still"). A final that goes on from the locked words it
 * follows is the normal landing; one that heard them differently, or a newer revision of a shown final with other
 * words (the ASR's refined correction, transcript.corrected with source refined), replaces the segment's words and
 * counts a correction. A shown final, and a partial's locked words a final replaced, keep the size of the words first
 * shown (fit), so a correction never changes a row's height; a revision no newer than the one shown, or with the same
 * words, changes nothing. A partial's locked words never unlock. Segments that left the transcript leave the map.
 * Returns prev when nothing changed.
 *
 * Rows never split or move either: a segment keeps the row it was first shown in, and rows keep the order they first
 * appeared in. A new segment joins the newest row when that row is the same participant's and its last segment is
 * final (as transcriptRows always grouped); otherwise, and for a new gap, a new row goes at the bottom. So the rep's
 * "Mm-hmm" published after the caller's next sentence already joined the caller's row becomes its own row under it,
 * instead of cutting the caller's row in two and pushing the second half down.
 */
export function nextDisplayedText(prev: DisplayedText, transcript: (Pick<Transcript, 'segments'> & Partial<Pick<Transcript, 'gaps'>>) | null): DisplayedText {
  if (!transcript?.segments.length && !transcript?.gaps?.length) return prev.size ? NO_DISPLAYED : prev;
  let changed = false;
  const next = new Map<string, DisplayedSegment>();
  const items: { at: number; order: number; seg?: Segment; gap?: string }[] = [];
  transcript.segments.forEach((s, i) => items.push({ at: s.start_ms, order: i, seg: s }));
  (transcript.gaps ?? []).forEach((g, i) => items.push({ at: g.start_ms, order: 100_000 + i, gap: gapKey(g) }));
  items.sort((a, b) => a.at - b.at || a.order - b.order);
  let maxSeq = 0, stamp = 0;
  for (const e of prev.values()) {
    if (e.seq !== undefined && e.seq > maxSeq) maxSeq = e.seq;
    stamp = Math.max(stamp, e.shownAt ?? 0, e.correctedAt ?? 0);
  }
  // The newest row in display order: its seq, whose turn it is (null for a gap) and whether its last segment is final.
  type Tail = { row: string; seq: number; participant: string | null; final: boolean };
  let tail: Tail | null = null;
  const touch = (t: Tail) => { if (!tail || t.seq >= (tail as Tail).seq) tail = t; };
  for (const item of items) {
    if (item.gap) {
      if (next.has(item.gap)) continue;
      const before = prev.get(item.gap);
      const entry = before?.seq !== undefined ? before : { text: '', final: true, row: item.gap, seq: ++maxSeq };
      if (entry !== before) changed = true;
      next.set(item.gap, entry);
      touch({ row: item.gap, seq: entry.seq!, participant: null, final: true });
      continue;
    }
    const s = item.seg!;
    const before = prev.get(s.id);
    let text: Omit<DisplayedSegment, 'row' | 'seq'>;
    if (s.is_final) text = finalShown(before, s);
    else {
      const fresh = committedPrefix(s);
      text = before && !before.final && before.text.length >= fresh.length && s.text.startsWith(before.text) ? before : { text: fresh, final: false };
    }
    if (text !== before) {
      // The stamps: a correction keeps when the words were shown and records its first time; any other change of the
      // words shown is words joining the row now.
      const { shownAt, correctedAt } = before ?? {};
      const corrected = (text.corrections ?? 0) > (before?.corrections ?? 0);
      const at = corrected ? shownAt : text.text !== before?.text ? ++stamp : shownAt;
      const firstFix = corrected ? correctedAt ?? ++stamp : correctedAt;
      text = { ...text, ...(at === undefined ? {} : { shownAt: at }), ...(firstFix === undefined ? {} : { correctedAt: firstFix }) };
    }
    let row = before?.row, seq = before?.seq;
    if (row === undefined || seq === undefined) {
      const t = tail as Tail | null;
      if (t && s.participant_id && t.participant === s.participant_id && t.final) { row = t.row; seq = t.seq; }
      else { row = s.id; seq = ++maxSeq; }
    }
    const entry: DisplayedSegment = before && text === before && before.row === row && before.seq === seq ? before : { ...text, row, seq };
    if (entry !== before) changed = true;
    next.set(s.id, entry);
    // Items come in time order, so this segment is its row's newest so far.
    touch({ row, seq, participant: s.participant_id ?? null, final: s.is_final });
  }
  return !changed && next.size === prev.size ? prev : next;
}

/** A final's words as its row shows them, given what the row showed for the segment before. */
function finalShown(before: DisplayedSegment | undefined, s: Segment): Omit<DisplayedSegment, 'row' | 'seq'> {
  if (!before) return { text: s.text, final: true, rev: s.revision };
  if (!before.final) {
    // The partial's locked words turn final: the final going on from them is the normal landing (the tail joins the
    // row); a final that heard them differently (a refined final straight after the locked words) replaces them, the
    // row settles and keeps the box of the locked words it showed, as a corrected final does (integration review: rows
    // never move, the list never scrolls).
    const locked = before.text.trim();
    if (!locked || s.text.trim().startsWith(locked)) return { text: s.text, final: true, rev: s.revision };
    return { text: s.text, final: true, rev: s.revision, fit: before.text, corrections: 1 };
  }
  if (s.revision <= (before.rev ?? 0) || s.text.trim() === before.text.trim()) return before;
  // A newer revision with other words (the refined correction): the words change in place and the row keeps the size
  // of the words it first showed.
  const fit = before.fit ?? before.text;
  return { text: s.text, final: true, rev: s.revision, ...(fit.trim() === s.text.trim() ? {} : { fit }), corrections: (before.corrections ?? 0) + 1 };
}

type Shown = { committed: string; pending: string; fit?: string; corrections?: number; shownAt?: number; correctedAt?: number };
const stampsOf = (memo: DisplayedSegment | undefined) => ({ ...(memo?.shownAt === undefined ? {} : { shownAt: memo.shownAt }), ...(memo?.correctedAt === undefined ? {} : { correctedAt: memo.correctedAt }) });
function displayOf(s: Segment, displayed: DisplayedText | undefined): Shown {
  const memo = displayed?.get(s.id);
  if (s.is_final) {
    if (!memo?.final) return { committed: s.text.trim(), pending: '' };
    return { committed: memo.text.trim(), pending: '', ...(memo.fit === undefined ? {} : { fit: memo.fit.trim() }), ...(memo.corrections ? { corrections: memo.corrections } : {}), ...stampsOf(memo) };
  }
  const fresh = committedPrefix(s);
  const locked = memo && !memo.final && memo.text.length > fresh.length && s.text.startsWith(memo.text) ? memo.text : fresh;
  return { committed: locked.trim(), pending: s.text.slice(locked.length).trim(), ...stampsOf(memo) };
}

/** A row's words, joined from its segments in time order: what it shows, the words its box is sized for, when its
 * first correction came (fixedAt, the earliest correctedAt of a segment still sized for other words) and when words
 * last joined it (joinedAt, the newest shownAt of a segment with words), and its corrections. The row has grown when
 * words joined after its first correction, whichever segment of the turn was corrected and wherever the joining words
 * sit: a correction to a segment that is not the turn's last holds the row as one to its last does (integration
 * review), and once words have joined, a further correction of a segment already corrected never holds it again, so
 * it never shrinks the row. */
type Words = { text: string; fit: string; fitted: boolean; fixedAt: number; joinedAt: number; corrections: number };
const joinWords = (a: string, b: string) => (a && b ? `${a} ${b}` : a || b);
const noWords = (): Words => ({ text: '', fit: '', fitted: false, fixedAt: Infinity, joinedAt: 0, corrections: 0 });
function addWords(w: Words, shown: Shown): void {
  w.text = joinWords(w.text, shown.committed);
  w.fit = joinWords(w.fit, shown.fit ?? shown.committed);
  if (shown.fit !== undefined) { w.fitted = true; w.fixedAt = Math.min(w.fixedAt, shown.correctedAt ?? 0); }
  if (shown.committed) w.joinedAt = Math.max(w.joinedAt, shown.shownAt ?? 0);
  w.corrections += shown.corrections ?? 0;
}
/** The row fields a correction adds; none on a row no correction touched. */
const correctionOf = (w: Words): Pick<SegRow, 'fit' | 'held' | 'corrections'> => ({
  ...(w.fitted && w.fit !== w.text ? { fit: w.fit, ...(w.joinedAt > w.fixedAt ? {} : { held: true }) } : {}),
  ...(w.corrections ? { corrections: w.corrections } : {}),
});

/** Whether a row is drawn: a gap, or a turn with words in its box (a turn with no locked words yet is all on the
 * pending line). A row sized for no words stays undrawn, as it was, so it never appears between rows. */
export function rowShown(row: TranscriptRow): boolean {
  return row.kind === 'gap' || (row.fit ?? row.text) !== '';
}

export interface TranscriptRowOptions {
  /** The prospect's first name from the CRM card; "Caller" without one. */
  callerName?: string | null;
  displayed?: DisplayedText;
}

export function gapRowText(gap: Transcript['gaps'][number]): string {
  const span = `${formatDuration(gap.start_ms)} to ${formatDuration(gap.end_ms)}`;
  return gap.reason === 'privacy_pause' ? `Transcription paused ${span}` : `Not transcribed ${span}`;
}

/**
 * Segments and gaps in time order, newest at the bottom, one row per speaker turn: a known participant's next final
 * segment joins its turn's row (a gap or another speaker starts a new one). A turn keeps the key of its first segment,
 * so a provisional segment keeps its row when it turns final and more speech joins it.
 */
export function transcriptRows(transcript: Pick<Transcript, 'segments' | 'gaps'> | null, participants: readonly Participant[], selfUserId: string | null, options: TranscriptRowOptions = {}): TranscriptRow[] {
  if (!transcript) return [];
  const memo = options.displayed;
  if (memo && transcript.segments.every(s => memo.get(s.id)?.seq !== undefined) && transcript.gaps.every(g => memo.get(gapKey(g))?.seq !== undefined)) {
    return rowsFromMemo(transcript, participants, selfUserId, memo, options.callerName ?? null);
  }
  const items: { at: number; order: number; seg?: Segment; gap?: TranscriptRow }[] = [];
  transcript.segments.forEach((s, i) => items.push({ at: s.start_ms, order: i, seg: s }));
  transcript.gaps.forEach((g, i) => items.push({ at: g.start_ms, order: 100_000 + i, gap: { key: `gap-${g.start_ms}-${g.end_ms}-${i}`, kind: 'gap', text: gapRowText(g) } }));
  items.sort((a, b) => a.at - b.at || a.order - b.order);
  const rows: TranscriptRow[] = [];
  const turns: { row: SegRow; words: Words }[] = [];
  let open: { row: SegRow; participantId: string; words: Words } | null = null;
  for (const item of items) {
    const s = item.seg;
    if (!s) { rows.push(item.gap!); open = null; continue; }
    const shown = displayOf(s, options.displayed);
    if (open && s.participant_id === open.participantId && !open.row.partial) {
      addWords(open.words, shown);
      open.row.text = open.words.text;
      open.row.pending = shown.pending;
      open.row.partial = !s.is_final;
      continue;
    }
    const { role, who } = speakerOf(participants, s.participant_id, selfUserId, options.callerName ?? null);
    const words = noWords();
    addWords(words, shown);
    const row: SegRow = { key: s.id, kind: 'seg', role, who, text: words.text, pending: shown.pending, partial: !s.is_final, revision: s.revision };
    rows.push(row);
    turns.push({ row, words });
    open = s.participant_id ? { row, participantId: s.participant_id, words } : null;
  }
  for (const { row, words } of turns) Object.assign(row, correctionOf(words));
  return rows;
}

/** Rows as they were first shown (nextDisplayedText's row and seq): each segment in its own row, rows in the order they
 * appeared, a row's segments in time order. Never splits a row or moves one. */
function rowsFromMemo(transcript: Pick<Transcript, 'segments' | 'gaps'>, participants: readonly Participant[], selfUserId: string | null, memo: DisplayedText, callerName: string | null): TranscriptRow[] {
  const groups = new Map<string, { seq: number; gap?: TranscriptRow; segs: Segment[] }>();
  transcript.gaps.forEach((g, i) => {
    const e = memo.get(gapKey(g))!;
    if (!groups.has(e.row!)) groups.set(e.row!, { seq: e.seq!, gap: { key: `gap-${g.start_ms}-${g.end_ms}-${i}`, kind: 'gap', text: gapRowText(g) }, segs: [] });
  });
  for (const s of transcript.segments) {
    const e = memo.get(s.id)!;
    const group = groups.get(e.row!) ?? { seq: e.seq!, segs: [] };
    group.segs.push(s);
    groups.set(e.row!, group);
  }
  const rows: TranscriptRow[] = [];
  for (const [key, group] of [...groups].sort((a, b) => a[1].seq - b[1].seq)) {
    if (group.gap) { rows.push(group.gap); continue; }
    const segs = group.segs.sort((a, b) => a.start_ms - b.start_ms);
    const first = segs.find(x => x.id === key) ?? segs[0]!;
    const { role, who } = speakerOf(participants, first.participant_id, selfUserId, callerName);
    const words = noWords();
    let pending = '';
    for (const x of segs) {
      const shown = displayOf(x, memo);
      addWords(words, shown);
      pending = shown.pending;
    }
    rows.push({ key, kind: 'seg', role, who, text: words.text, pending, partial: !segs.at(-1)!.is_final, revision: first.revision, ...correctionOf(words) });
  }
  return rows;
}

/** The transcript's reserved last line: the unlocked tail of the speech in progress, the prospect's first when two
 * speak at once. named: its turn is not the last row on screen, so the line carries the speaker's label. */
export interface PendingLine { key: string; role: SpeakerRole; who: string; text: string; named: boolean }

export function pendingLine(rows: readonly TranscriptRow[]): PendingLine | null {
  let pick: SegRow | null = null;
  for (const r of rows) {
    if (r.kind !== 'seg' || !r.partial || !r.pending) continue;
    if (!pick || r.role === 'caller' || pick.role !== 'caller') pick = r;
  }
  if (!pick) return null;
  const lastShown = rows.findLast(rowShown);
  return { key: pick.key, role: pick.role, who: pick.who, text: pick.pending, named: lastShown?.key !== pick.key };
}

/** The caller's newest final line: what the say-next line answers, as its row shows it (a correction's words once the
 * final decode disagreed). */
export function lastCallerLine(transcript: Pick<Transcript, 'segments'> | null, participants: readonly Participant[], displayed?: DisplayedText): string | null {
  if (!transcript) return null;
  let best: Segment | null = null;
  for (const s of transcript.segments) {
    if (!s.is_final || !s.text.trim()) continue;
    const p = s.participant_id ? participants.find(x => x.id === s.participant_id) : undefined;
    if (p?.kind !== 'external') continue;
    if (!best || s.start_ms > best.start_ms || (s.start_ms === best.start_ms && s.id.localeCompare(best.id) > 0)) best = s;
  }
  if (!best) return null;
  const memo = displayed?.get(best.id);
  return (memo?.final ? memo.text : best.text).trim() || null;
}

/** The word beside "Transcript". */
export function transcriptStateWord(call: Pick<Call, 'transcription_state' | 'recording_state' | 'state'> | null, streamLive: boolean): string {
  if (!call) return '';
  switch (call.transcription_state) {
    case 'live': return streamLive ? 'Live' : 'Reconnecting';
    case 'pending': return 'Starting';
    case 'delayed': return 'Delayed';
    case 'failed': return 'Failed';
    case 'complete': return 'Complete';
    default: return call.state === 'ended' || call.state === 'failed' ? 'Off' : 'Off until recording starts';
  }
}

// ---- Say next ----

/** A sayable quick-action answer held in the say-next slot until new speech has been coached. */
export interface SayPin { label: string; line1: string; line2: string; segmentsUsed: number }

export interface SayNextInput {
  phase: CallWindowPhase;
  callState: Call['state'] | null;
  /** transcript.coach usable for this person and media access ready. */
  coachAvailable: boolean;
  coachEnabled: boolean;
  coach: Pick<CoachState, 'result' | 'error'> & Partial<Pick<CoachState, 'offHours'>>;
  /** The last lines from before Hold, shown muted while the call is held. */
  frozen: readonly string[] | null;
  pin: SayPin | null;
  /** A quick action's own outcome, such as "Coach couldn't answer that". */
  askStatus: string | null;
  /** Wall clock, for the off-hours line's weekday. */
  now?: number;
}

export interface SayNextModel {
  label: string;
  line1: string;
  line2: string;
  line3: string;
  status: string;
  statusTone: 'quiet' | 'warn';
  /** No line to say yet: say-1 shows its quiet placeholder in the same box. */
  placeholder: boolean;
  /** No line will come while this lasts, and the status line says why (Coach off, not available, off hours, the call is
   * over, the window has nothing to show): say-1 stays empty, without its "Your next line shows here." promise. */
  off: boolean;
  /** Frozen lines (Hold): shown, but muted. */
  muted: boolean;
  /** The tip no longer covers the latest speech (a correction, a "not yet" answer, a failure, off hours): it stays, dimmed,
   * until the next tip replaces it. */
  stale: boolean;
}

export const SAY_PLACEHOLDER = 'Your next line shows here.';

export function sayNextModel(input: SayNextInput): SayNextModel {
  const { phase, callState, coach, pin, frozen } = input;
  const live = callState === 'active';
  const lines = live && coach.result ? coach.result.lines : [];
  let label = 'Say next', line1 = '', line2 = '', line3 = '', muted = false, stale = false;
  if (phase === 'ready' && live && pin) {
    label = pin.label; line1 = pin.line1; line2 = pin.line2;
  } else if (phase === 'ready' && lines.length) {
    line1 = lines[0] ?? ''; line2 = lines[1] ?? ''; line3 = lines[2] ?? '';
    stale = coach.result?.stale === true;
  } else if (phase === 'ready' && callState === 'held' && frozen && frozen.length) {
    line1 = frozen[0] ?? ''; line2 = frozen[1] ?? ''; line3 = frozen[2] ?? ''; muted = true;
  }
  let status = '', statusTone: SayNextModel['statusTone'] = 'quiet';
  if (phase === 'signed_out') status = 'Sign in again in the main window';
  else if (phase === 'unavailable') status = 'This call isn’t available';
  else if (phase === 'moved') status = 'Showing in a newer call window';
  else if (phase === 'failed_start') status = 'The call didn’t start';
  else if (callState === 'ended' || callState === 'failed') status = 'Call ended';
  else if (callState === 'held') status = 'On hold';
  else if (phase !== 'ready' || callState === null || callState === 'created' || callState === 'ringing') status = callState === 'ringing' ? 'Starts when they answer' : '';
  else if (!input.coachAvailable) status = 'Coach isn’t available';
  else if (!input.coachEnabled && !pin) status = 'Coach is off';
  else if (coach.offHours) status = coachOffHoursLine(coach.offHours.until, input.now);
  else if (coach.error) { status = 'Coach unavailable, retrying'; statusTone = 'warn'; }
  else if (input.askStatus) status = input.askStatus;
  const over = phase === 'signed_out' || phase === 'unavailable' || phase === 'moved' || phase === 'failed_start' || callState === 'ended' || callState === 'failed';
  const coachOff = phase === 'ready' && live && (!input.coachAvailable || (!input.coachEnabled && !pin) || !!coach.offHours);
  return { label, line1, line2, line3, status, statusTone, placeholder: !line1, off: !line1 && (over || coachOff), muted, stale };
}

/** The wrap-up may replace say-next only for a call that was answered and then ended (not one that failed, never
 * connected, or that only my own leg left while it carries on). The window shows it only once one exists or the server
 * says one is coming (CallWindowRoot); otherwise say-next stays, saying "Call ended". */
export function wrapUpShown(phase: CallWindowPhase, call: Pick<Call, 'state' | 'answered_at'> | null): boolean {
  return phase === 'ready' && !!call && call.state === 'ended' && !!call.answered_at;
}

/** Whether the summary job will write this call's wrap-up rather than skip it, by the job's own rule
 * (apps/api/src/summary/job.ts skipReason): under CALL_SUMMARY_MIN_TALK_MS of talk (the controller's talk time, else
 * answered to ended) is too_short unless a voicemail was dropped, and a call that was never transcribed is
 * no_transcript. While such a call's wrap-up is "written" the box promises nothing (review round 3: Krisp gives calls
 * under 30 seconds no summary at all, and a rep sees dozens of short connects a day). An answering machine the job hears
 * in the transcript is still written, and shows when it lands. */
export function wrapUpExpected(call: Pick<Call, 'talk_ms' | 'answered_at' | 'ended_at' | 'outcome' | 'voicemail_drop' | 'transcription_state'> | null): boolean {
  if (!call) return false;
  if (call.transcription_state === 'off') return false;
  const answered = call.answered_at ? Date.parse(call.answered_at) : NaN, ended = call.ended_at ? Date.parse(call.ended_at) : NaN;
  const talk = call.talk_ms > 0 ? call.talk_ms : Number.isFinite(answered) && Number.isFinite(ended) ? Math.max(0, ended - answered) : 0;
  const voicemail = !!call.voicemail_drop || call.outcome === 'voicemail_dropped' || call.outcome === 'voicemail_drop_interrupted';
  return talk >= CALL_SUMMARY_MIN_TALK_MS || voicemail;
}

/** Why the question box is off, in the box's own reserved line; null while asking works (or while one is in flight).
 * Off hours writes nothing here: say-status already says "Coach is off until 7:00" once, and the field keeps its plain
 * placeholder while it is off (review round 2). The field is still visibly off then: read-only, in the muted ink of
 * the chips beside it (QuestionThread, review round 3), so a question is never typed and then silently ignored. */
export function askOffReason(input: { phase: CallWindowPhase; callState: Call['state'] | null; coachAvailable: boolean; offHours: CoachState['offHours']; now?: number }): string | null {
  if (input.phase !== 'ready') return null;
  if (!input.coachAvailable) return 'Coach isn’t available';
  switch (input.callState) {
    case 'active': return null;
    case 'held': return 'On hold';
    case 'ended': case 'failed': return 'The call has ended';
    default: return 'Opens when they answer';
  }
}

/** A pinned answer gives way once a newer auto-tip has used more speech than the answer had seen. */
export function pinCleared(pin: SayPin | null, result: CoachResponse | null): boolean {
  return !!pin && !!result && result.lines.length > 0 && result.segments_used > pin.segmentsUsed;
}

// ---- CRM pane ----

export interface CrmPaneModel {
  /** 'own': the call's card; 'previous': the last call's card with this number; 'awaited': looking; 'none': no card
   * expected; 'unknown': the CRM does not know this number. */
  state: 'own' | 'previous' | 'awaited' | 'none' | 'unknown';
  title: string | null;
  provenance: string;
  /** Three fixed rows; null is a quiet dash once a card is here, or a placeholder bar before it arrives. */
  who: [string | null, string | null, string | null];
  nextStep: string | null;
  details: { label: string; value: string }[];
  history: { summary: string; when: string }[];
  primary: ContextLink | null;
  /** Nothing has arrived yet: rows show placeholder bars, not dashes. */
  waiting: boolean;
  /** The call's own CRM card's one-click dispositions (the CRM ledger write-back). None for a card carried from the last
   * call, an unknown number or a generic card: those belong to the call's own candidate card, as in the dock's card. */
  dispositions: { id: string; label: string }[];
  /** The disposition the card already carries, if any. */
  disposition: string | null;
}

export function crmPaneModel(
  own: ContextCardModel | null,
  previous: { model: ContextCardModel; fromCallAt: string | null } | null,
  expected: boolean,
  now: number,
): CrmPaneModel {
  const model = own ?? previous?.model ?? null;
  const state: CrmPaneModel['state'] = own ? (own.kind === 'unknown' ? 'unknown' : 'own') : previous ? 'previous' : expected ? 'awaited' : 'none';
  if (!model) {
    return {
      state, title: null,
      provenance: expected ? 'Waiting for the CRM card' : 'No CRM card for this call',
      who: [null, null, null], nextStep: null, details: [], history: [], primary: null, waiting: expected,
      dispositions: [], disposition: null,
    };
  }
  const ownCandidate = state === 'own' && model.kind === 'candidate';
  const updated = model.updatedAt ? formatRelative(model.updatedAt, now) : '';
  const provenance = state === 'unknown'
    ? 'Not in the CRM'
    : state === 'previous'
      ? `From the last call with this number${previous?.fromCallAt ? ` (${formatRelative(previous.fromCallAt, now)})` : ''}`
      : `From ${model.source}${updated ? `, updated ${updated}` : ''}`;
  const w = model.who;
  const person = w?.contactName ? [w.contactName, w.contactRole].filter(Boolean).join(', ') : w?.contactRole ?? null;
  const standing = w ? [w.stage ? `Stage ${w.stage}` : null, w.owner ? `owner ${w.owner}` : null].filter(Boolean).join(', ') : '';
  return {
    state,
    title: model.title,
    provenance,
    who: [person || null, w?.company ?? null, standing || null],
    nextStep: model.nextStep,
    details: model.fields.slice(0, 12),
    history: (model.history ?? []).slice(0, 3).map(h => ({ summary: h.summary, when: h.at ? formatRelative(h.at, now) : '' })),
    primary: model.links[0] ?? null,
    waiting: false,
    dispositions: ownCandidate ? model.dispositions.map(d => ({ id: d.id, label: d.label })) : [],
    disposition: ownCandidate ? w?.disposition ?? null : null,
  };
}

/** The disposition slot's status line: what happened to the last choice. Nothing while it is being saved (the pressed
 * button carries aria-busy), so no "Saving…" comes and goes. */
export function dispositionStatus(_crm: Pick<CrmPaneModel, 'dispositions' | 'waiting'>, status: string | null): string {
  // No dispositions apply (no card, a card from the last call, a generic or contact card): the slot's quiet dash says
  // so, with no line of its own (review round 2: "Shows with the call's own CRM card" was false on the call's own card).
  return status ?? '';
}

export function crmOpenStatus(primary: ContextLink | null, blocked: boolean, pageHost: string | null): string {
  if (!primary) return 'No CRM link on this card';
  if (blocked) return `Pop-up blocked. Allow pop-ups for ${pageHost || 'sidet.one'}.`;
  return primary.host;
}

/** Picks the call the window should switch to during call waiting: a newer call of mine that is now connected while the
 * shown one is on hold. Anything else keeps the window on the call it shows. */
export function pickCallWindowCall(current: Call | null, candidate: Call, selfUserId: string | null): boolean {
  if (!current || candidate.id === current.id || !selfUserId) return false;
  if (candidate.state !== 'active') return false;
  if (!candidate.participants.some(p => p.user_id === selfUserId && p.kind === 'agent' && !p.left_at)) return false;
  if (candidate.created_at <= current.created_at) return false;
  return current.state === 'held';
}

/** In a pending window, the first outbound call of mine that started after the Dial: the binding fallback when the
 * main window reloaded before it could say which call it was. */
export function selfSelectPending(candidate: Call, selfUserId: string | null, launchedAt: number, known: ReadonlySet<string>): boolean {
  if (!selfUserId || known.has(candidate.id) || candidate.direction !== 'outbound') return false;
  if (!['created', 'ringing', 'active', 'held'].includes(candidate.state)) return false;
  if (!candidate.participants.some(p => p.user_id === selfUserId && p.kind === 'agent' && !p.left_at)) return false;
  const created = Date.parse(candidate.created_at);
  return Number.isFinite(created) && created >= launchedAt - 10_000;
}
